import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FiSearch,
  FiDownload,
  FiChevronDown,
  FiPlayCircle,
} from "react-icons/fi";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import TmisLayout from "../components/TmisLayout";

const API_BASE = process.env.REACT_APP_API || "http://localhost:3023/api";

const API_ENDPOINTS = {
  getIntakes: `${API_BASE}/getIntakes`,
  getTrainers: `${API_BASE}/getTrainers`,
  getStudents: `${API_BASE}/getStudents`,
};

const getAuthToken = () =>
  localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

const getAxiosConfig = () => {
  const token = getAuthToken();
  if (!token) return {};
  return { headers: { Authorization: `Bearer ${token}` } };
};

const getInitials = (name = "") => {
  const initials = name
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();

  return initials || "U";
};

const getLoginUser = () => {
  try {
    const storedUser =
      localStorage.getItem("user") || sessionStorage.getItem("user");

    if (!storedUser) {
      return {
        name: "User",
        role: "trainer",
        email: "",
        initials: "U",
      };
    }

    const user = JSON.parse(storedUser);

    return {
      ...user,
      name: user.name || user.user_name || user.display_name || "User",
      role: user.role || "trainer",
      email: user.email || "",
      initials:
        user.initials ||
        getInitials(user.name || user.user_name || user.display_name),
    };
  } catch (error) {
    console.error("Failed to read login user:", error);

    return {
      name: "User",
      role: "trainer",
      email: "",
      initials: "U",
    };
  }
};

const DashboardSummaryPanel = ({ total, completed, undone, chartData }) => {
  return (
    <aside className="dashboard-summary-panel">
      <div className="summary-card">
        <h3>Summary</h3>

        <div className="summary-grid">
          <div className="summary-item">
            <span>Total</span>
            <strong>{total}</strong>
          </div>

          <div className="summary-item completed">
            <span>Completed</span>
            <strong>{completed}</strong>
          </div>

          <div className="summary-item undone">
            <span>Undone</span>
            <strong>{undone}</strong>
          </div>
        </div>
      </div>

      <div className="summary-card chart-card">
        <h3>Assessment Trend</h3>

        <div className="line-chart-box">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="total"
                stroke="#1f3b8c"
                strokeWidth={3}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="completed"
                stroke="#16a34a"
                strokeWidth={3}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="undone"
                stroke="#dc2626"
                strokeWidth={3}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </aside>
  );
};

const TmisDashboard = () => {
  const loginUser = useMemo(() => getLoginUser(), []);
  const isAdmin = String(loginUser.role || "").toLowerCase() === "admin";

  const [classCode, setClassCode] = useState("");
  const [trainer, setTrainer] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [searchText, setSearchText] = useState("");

  const [intakes, setIntakes] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [studentsFromDb, setStudentsFromDb] = useState([]);

  const [loadingFilters, setLoadingFilters] = useState(false);
  const [filterMessage, setFilterMessage] = useState("");

  const students = useMemo(() => {
    return studentsFromDb.map((item) => ({
      id: item.id,
      name: item.name || "-",
      course:
        item.guiding_language || item.language || "TMIS Tourist Guide Training",
      duration: "-",
      trainer: "-",
      attendance: "0%",
      status: "Undone",
      classCode: item.intake || "",
      email: item.email || "",
      phone: item.phone || "",
      nric: item.nric || "",
      gender: item.gender || "",
      citizenship: item.citizenship || "",
      nationality: item.nationality || "",
      intake: item.intake || "",
      language: item.language || "",
      guiding_language: item.guiding_language || "",
    }));
  }, [studentsFromDb]);

  const loadFilters = useCallback(async () => {
    setLoadingFilters(true);
    setFilterMessage("");

    try {
      const [intakeResponse, trainerResponse, studentResponse] =
        await Promise.all([
          axios.get(API_ENDPOINTS.getIntakes, getAxiosConfig()),
          axios.get(API_ENDPOINTS.getTrainers, getAxiosConfig()),
          axios.get(API_ENDPOINTS.getStudents, getAxiosConfig()),
        ]);

      setIntakes(
        Array.isArray(intakeResponse.data?.data)
          ? intakeResponse.data.data
          : []
      );

      setTrainers(
        Array.isArray(trainerResponse.data?.data)
          ? trainerResponse.data.data
          : []
      );

      setStudentsFromDb(
        Array.isArray(studentResponse.data?.data)
          ? studentResponse.data.data
          : []
      );
    } catch (error) {
      console.error("Load filters error:", error);
      setFilterMessage(
        error.response?.data?.message ||
          "Failed to load intake, trainer or student list."
      );
    } finally {
      setLoadingFilters(false);
    }
  }, []);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  const trainerNameForTrainerLogin = loginUser.name;

  const filteredStudents = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();

    return students.filter((studentItem) => {
      const matchKeyword =
        !keyword ||
        studentItem.name.toLowerCase().includes(keyword) ||
        studentItem.course.toLowerCase().includes(keyword) ||
        studentItem.email.toLowerCase().includes(keyword) ||
        studentItem.phone.toLowerCase().includes(keyword) ||
        studentItem.intake.toLowerCase().includes(keyword);

      const matchClass =
        !classCode || String(studentItem.classCode) === String(classCode);

      const matchStudent =
        !selectedStudent || String(studentItem.id) === String(selectedStudent);

      const matchTrainer = isAdmin
        ? !trainer || studentItem.trainer === trainer
        : true;

      return matchKeyword && matchClass && matchStudent && matchTrainer;
    });
  }, [searchText, trainer, classCode, selectedStudent, isAdmin, students]);

  const completedCount = filteredStudents.filter(
    (s) => s.status === "Completed"
  ).length;

  const undoneCount = filteredStudents.filter(
    (s) => s.status === "Undone"
  ).length;

  const chartData = useMemo(() => {
    return [
      {
        name: "Week 1",
        total: filteredStudents.length,
        completed: Math.max(completedCount - 2, 0),
        undone: undoneCount + 2,
      },
      {
        name: "Week 2",
        total: filteredStudents.length,
        completed: Math.max(completedCount - 1, 0),
        undone: undoneCount + 1,
      },
      {
        name: "Week 3",
        total: filteredStudents.length,
        completed: completedCount,
        undone: undoneCount,
      },
    ];
  }, [filteredStudents.length, completedCount, undoneCount]);

  const handleSearch = () => {
    console.log({
      classCode,
      student: selectedStudent,
      trainer: isAdmin ? trainer : trainerNameForTrainerLogin,
      searchText,
      role: loginUser.role,
    });
  };

  const handleExport = () => {
    console.log("Export clicked");
  };

  const handleTakeAction = (student) => {
    console.log("Take Action clicked", student);
  };

  return (
    <TmisLayout
      searchText={searchText}
      onSearchChange={setSearchText}
      rightPanel={
        <DashboardSummaryPanel
          total={filteredStudents.length}
          completed={completedCount}
          undone={undoneCount}
          chartData={chartData}
        />
      }
    >
      <div className="filter-card accessible-card">
        <div className="section-heading-row">
          <div>
            <h1>Student Assessment</h1>
            <p>Review student progress, attendance and current status.</p>
          </div>
        </div>

        {filterMessage && (
          <div className="template-message warning">{filterMessage}</div>
        )}

        <div className="filter-row dashboard-filter-row">
          <label className="select-group">
            <span>Intake Code</span>
            <div className="select-wrapper">
              <select
                value={classCode}
                onChange={(e) => setClassCode(e.target.value)}
                disabled={loadingFilters}
              >
                <option value="">
                  {loadingFilters ? "Loading intakes..." : "All Intake"}
                </option>

                {intakes.map((item) => (
                  <option key={item.id} value={item.intake_code}>
                    {item.intake_code}
                  </option>
                ))}
              </select>
              <FiChevronDown />
            </div>
          </label>

          <label className="select-group">
            <span>Student</span>
            <div className="select-wrapper">
              <select
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
                disabled={loadingFilters}
              >
                <option value="">
                  {loadingFilters ? "Loading students..." : "All Students"}
                </option>

                {studentsFromDb.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              <FiChevronDown />
            </div>
          </label>

          {isAdmin && (
            <label className="select-group">
              <span>Trainer</span>
              <div className="select-wrapper">
                <select
                  value={trainer}
                  onChange={(e) => setTrainer(e.target.value)}
                  disabled={loadingFilters}
                >
                  <option value="">
                    {loadingFilters ? "Loading trainers..." : "All Trainers"}
                  </option>

                  {trainers.map((item) => (
                    <option key={item.id} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
                <FiChevronDown />
              </div>
            </label>
          )}

          <button className="search-btn dashboard-search-btn" type="button" onClick={handleSearch}>
            <FiSearch /> Search
          </button>
        </div>
      </div>

      <section className="review-card accessible-card">
        <div className="card-header">
          <div>
            <h2>Ongoing Assessment</h2>
          </div>

          <button
            className="blue-outline-btn"
            type="button"
            onClick={handleExport}
          >
            <FiDownload /> Export
          </button>
        </div>

        <div className="table-responsive">
          <table className="performance-table">
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Course</th>
                <th>Intake</th>
                <th>Email</th>
                <th>Attendance</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-table-message">
                    No student records found.
                  </td>
                </tr>
              ) : (
                filteredStudents.map((student, index) => (
                  <tr key={`${student.id}-${index}`}>
                    <td data-label="Student Name">
                      <div className="student-cell">
                        <span
                          className={
                            student.status === "Completed"
                              ? "status-dot green"
                              : "status-dot red"
                          }
                        ></span>
                        {student.name}
                      </div>
                    </td>

                    <td data-label="Course">{student.course}</td>
                    <td data-label="Intake">{student.intake || "-"}</td>
                    <td data-label="Email">{student.email || "-"}</td>

                    <td data-label="Attendance">
                      <div className="progress-cell">
                        <strong>{student.attendance}</strong>
                        <div className="progress-bar" aria-hidden="true">
                          <div style={{ width: student.attendance }}></div>
                        </div>
                      </div>
                    </td>

                    <td data-label="Status">
                      <span
                        className={`status-badge ${
                          student.status === "Completed"
                            ? "completed"
                            : "undone"
                        }`}
                      >
                        {student.status}
                      </span>
                    </td>

                    <td data-label="Action">
                      {student.status === "Undone" ? (
                        <button
                          className="take-action-btn"
                          type="button"
                          onClick={() => handleTakeAction(student)}
                        >
                          <FiPlayCircle /> Take Action
                        </button>
                      ) : (
                        <span className="completed-text">Completed</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </TmisLayout>
  );
};

export default TmisDashboard;